import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * 중복을 접는 aria-live 요약 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 스트리밍 화면(SSE·라이브 로그·디버그 터미널)은 초당 여러 번 상태가 바뀝니다.
 * 그 변화를 그대로 live region에 흘리면 스크린리더가 쉬지 않고 말하게 되어
 * **아무것도 전달되지 않습니다.** 그래서 세 가지를 지킵니다.
 *
 *   접기      같은 key의 **같은 문장**은 다시 말하지 않습니다.
 *   묶기      짧은 창(FLUSH_MS) 안의 변화는 한 번에 모아 말합니다. 창 안에서
 *             같은 key가 여러 번 바뀌면 **마지막 문장만** 남습니다.
 *   유계      기억하는 key 수와 대기 중인 문장 수에 상한이 있습니다.
 *
 * 폴링하지 않습니다 — 대기 중인 문장이 있을 때만 타이머 하나가 돌고, 비우면
 * 스스로 멈춥니다. 언마운트에서 타이머를 정리하므로 남는 핸들이 없습니다.
 *
 * **이 영역이 스트리밍 낭독의 유일한 통로입니다.** 로그·터미널 원문은 각자
 * 화면에 그대로 남지만 `aria-live="off"`라 낭독되지 않습니다.
 */

/** 변화를 모으는 창. 사람이 "한 덩어리"로 듣는 길이입니다. */
export const FLUSH_MS = 800;

/** 기억하는 key 수 상한. 넘으면 가장 오래된 것부터 버립니다(맵 삽입 순서). */
export const MAX_TRACKED_KEYS = 32;

export type Announce = (key: string, message: string) => void;

const AnnouncerContext = createContext<Announce | undefined>(undefined);

const noop: Announce = () => {};

/**
 * 요약을 보낼 수 있는 함수. Provider 밖에서는 **조용히 무시**합니다 —
 * 알림 하나 때문에 화면이 죽는 편이 더 나쁩니다.
 */
export function useAnnounce(): Announce {
  return useContext(AnnouncerContext) ?? noop;
}

function trim(map: Map<string, string>, limit: number): void {
  while (map.size > limit) {
    const oldest = map.keys().next();
    if (oldest.done) return;
    map.delete(oldest.value);
  }
}

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState("");
  /** key별 **마지막으로 말한** 문장. 같은 문장은 다시 말하지 않습니다. */
  const spoken = useRef(new Map<string, string>());
  /** 이번 창에서 말할 문장. key당 하나만 남습니다. */
  const pending = useRef(new Map<string, string>());
  const timer = useRef<number | undefined>(undefined);

  const flush = useCallback(() => {
    timer.current = undefined;
    const queued = pending.current;
    if (queued.size === 0) return;
    const message = Array.from(queued.values()).join(" · ");
    for (const [key, value] of queued) spoken.current.set(key, value);
    queued.clear();
    trim(spoken.current, MAX_TRACKED_KEYS);
    setText(message);
  }, []);

  const announce = useCallback<Announce>(
    (key, message) => {
      const value = message.trim();
      if (value === "") return;
      if (spoken.current.get(key) === value && pending.current.get(key) === undefined) return;
      pending.current.set(key, value);
      trim(pending.current, MAX_TRACKED_KEYS);
      if (timer.current === undefined) timer.current = window.setTimeout(flush, FLUSH_MS);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
      timer.current = undefined;
    },
    [],
  );

  const value = useMemo(() => announce, [announce]);
  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      {/* 하나뿐인 polite 영역입니다. 화면마다 만들면 읽는 순서를 아무도 통제하지 못합니다. */}
      <p className="visually-hidden" role="status" aria-live="polite" data-testid="live-summary">
        {text}
      </p>
    </AnnouncerContext.Provider>
  );
}
